import { Injectable } from '@angular/core';
import { isNullOrUndefined, MessageService } from '@sla-ng/core';
import * as moment from 'moment';
import { BehaviorSubject, firstValueFrom, Observable, of, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ICostAccounting } from '../../../models/cost-accounting.model';
import { ICostCenterCostDistribution } from '../../../models/cost-center-cost-distribution.model';
import { ICostCenterCostRedistribution } from '../../../models/cost-center-cost-redistribution.model';
import { ICostCenterCost } from '../../../models/cost-center-cost.model';
import { ICostCenter } from '../../../models/cost-center.model';
import { ICostInflow } from '../../../models/cost-inflow.model';
import { ICostOutflow } from '../../../models/cost-outflow.model';
import { ICostType } from '../../../models/cost-type.model';
import { CostAccountingResource } from '../../../resources/cost-accounting.resource';
import { CostCenterResource } from '../../../resources/cost-center.resource';
import { CostTypeResource } from '../../../resources/cost-type.resource';
import { stringToMoment } from '../../../utils/moment-utils';

import { ICostCenterCostRebookingState } from './cost-center-cost-rebooking-state.model';

let _state: ICostCenterCostRebookingState = {
    distributionLookup: {}, inflowLookup: {}, outflowLookup: {},
    targetCostCenters: [],
    secondaryCostTypes: [],
    costCenterCosts: [],
    selectedCostAccounting: undefined,
    isLoading: false,
};

@Injectable({
    providedIn: 'root',
})
export class CostCenterCostRebookingFacade {
    protected store$: BehaviorSubject<ICostCenterCostRebookingState> = new BehaviorSubject<ICostCenterCostRebookingState>(_state);

    constructor(private myCostAccountingResource: CostAccountingResource,
                private myMessageService: MessageService,
                private myCostTypeResource: CostTypeResource,
                private myCostCenterResource: CostCenterResource) {
    }

    public getStateSnapshot(): ICostCenterCostRebookingState {
        return this.store$.value;
    }

    public subState(): Observable<ICostCenterCostRebookingState> {
        return this.store$.asObservable();
    }

    public setSelectedCostAccounting(selectedCostAccounting: ICostAccounting): void {
        this.updateState({ ..._state, selectedCostAccounting });
        void this.fetchValidCostCentersForDaterange(stringToMoment(selectedCostAccounting.dateRange.from), stringToMoment(selectedCostAccounting.dateRange.to));
    }

    public async fetchCostCenterCosts(): Promise<void> {
        const state: ICostCenterCostRebookingState = this.getStateSnapshot();
        if (isNullOrUndefined(state.selectedCostAccounting)) {
            this.myMessageService.warningTrans('app.actions.feedback.invalid', 'app.page.cost-center-cost-rebooking.no-selection.label');
            return;
        }
        this.setIsloading(true);
        const costCenterCostDistributions: ICostCenterCostDistribution[] = await firstValueFrom(
            this.myCostAccountingResource.getCostCenterCosts(state.selectedCostAccounting.id)).catch(() => []);
        const costCenterCosts: ICostCenterCost[] = this.transformCostCenterCost(costCenterCostDistributions);
        this.updateState({ ..._state, isLoading: false, costCenterCosts });
    }

    public async calculateOperationalAccountingSheet(): Promise<void> {
        const state: ICostCenterCostRebookingState = this.getStateSnapshot();
        if (isNullOrUndefined(state.selectedCostAccounting)) {
            this.myMessageService.warningTrans('app.actions.feedback.invalid', 'app.page.cost-center-cost-rebooking.no-selection.label');
            return;
        }
        try {
            this.setIsloading(true);
            await firstValueFrom(this.myCostAccountingResource.calculateOperationalAccountingSheet(state.selectedCostAccounting.id));
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-center-cost-rebooking.success.label');
            await this.fetchCostCenterCosts();
        } catch (e) {
            console.error(e);
        }
        this.setIsloading(false);
    }

    public async redistributeCostCenterCost(costCenterCostId: number, redistribution: ICostCenterCostRedistribution): Promise<void> {
        const state: ICostCenterCostRebookingState = this.getStateSnapshot();
        if (isNullOrUndefined(state.selectedCostAccounting)) {
            this.myMessageService.warningTrans('app.actions.feedback.invalid', 'app.page.cost-center-cost-rebooking.no-selection.label');
            return;
        }

        this.setIsloading(true);
        try {
            await firstValueFrom(this.myCostAccountingResource.redistributeCostCenterCost(state.selectedCostAccounting.id, costCenterCostId, redistribution));
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-center-cost-rebooking.success.label');
        } catch (e) {
            console.error(e);
        }
        this.setIsloading(false);
    }

    public clearState(): void {
        this.updateState({
            distributionLookup: {},
            inflowLookup: {},
            outflowLookup: {},
            targetCostCenters: [],
            secondaryCostTypes: [],
            costCenterCosts: [],
            selectedCostAccounting: undefined,
            isLoading: false,
        });
    }

    public fetchSecondaryCostTypes(): Observable<ICostType[]> {
        if (_state.secondaryCostTypes.length > 0) {
            return of(_state.secondaryCostTypes);
        }
        return this.myCostTypeResource.getCostTypes(false, true, false)
            .pipe(
                tap((secondaryCostTypes: ICostType[]) => this.updateState({ ..._state, secondaryCostTypes })),
                catchError(() => []),
            );
    }

    public findExistingCostCenterCostDistribution(costCenterId: number, costTypeId: number): ICostCenterCostDistribution {
        const state: ICostCenterCostRebookingState = this.getStateSnapshot();
        for (const costCenterCost of state.costCenterCosts) {
            for (const distribution of costCenterCost.distributions) {
                if (distribution.costCenter.id === costCenterId && distribution.costType.id === costTypeId) {
                    return distribution;
                }
            }
        }
        return undefined;
    }

    private updateState(state: ICostCenterCostRebookingState): void {
        this.store$.next(_state = state);
    }

    private setIsloading(isLoading: boolean): void {
        this.updateState({ ..._state, isLoading });
    }

    /**
     * Groups CostCenterCostDistributions by CostCenter and calculates the sum
     */
    private transformCostCenterCost(distributions: ICostCenterCostDistribution[]): ICostCenterCost[] {
        const costCenterCostMap: Map<number, ICostCenterCost> = new Map();

        const outflowLookup: Record<number, ICostOutflow> = {};
        const inflowLookup: Record<number, ICostInflow> = {};
        const distributionLookup: Record<number, ICostCenterCostDistribution> = {};

        for (const distribution of distributions) {
            distributionLookup[ distribution.id ] = distribution;
            for (const inflow of distribution.apportionmentInflows) {
                inflowLookup[ inflow.id ] = inflow;
            }
            for (const outflow of distribution.apportionmentOutflows) {
                outflowLookup[ outflow.id ] = outflow;
            }


            if (costCenterCostMap.has(distribution.costCenter.id)) {
                const existingCostCenterCost = costCenterCostMap.get(distribution.costCenter.id);
                existingCostCenterCost.distributions.push(distribution);
                existingCostCenterCost.costCt += distribution.costCt;
            } else {
                const newCostCenterCost: ICostCenterCost = {
                    costCt: distribution.costCt,
                    costCenter: distribution.costCenter,
                    distributions: [ distribution ],
                };
                costCenterCostMap.set(distribution.costCenter.id, newCostCenterCost);
            }
        }
        this.updateState({ ..._state, distributionLookup, inflowLookup, outflowLookup });
        return Array.from(costCenterCostMap.values());
    }

    private async fetchValidCostCentersForDaterange(from: moment.Moment, to: moment.Moment): Promise<void> {
        this.setIsloading(true);
        const targetCostCenters: ICostCenter[] = await firstValueFrom(this.myCostCenterResource.getCostCenters(from, to)).catch(() => []);
        this.updateState({ ..._state, isLoading: false, targetCostCenters });
    }
}

